import { useRef, useState } from 'react';
import {
  ChevronRight,
  CircleCheckBig,
  CircleX,
  List,
  Send,
  Trash2,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useGetAgreeableClausesQuery,
  useMarkClauseAsAgreeableMutation,
  useUnmarkClauseAsAgreeableMutation,
} from '@/apis/extended/agreeableClauseApi';
import {
  useConfirmNegotiationsMutation,
  useGetContractPrioritiesQuery,
  useToggleClausePriorityMutation,
} from '@/apis/extended/contractApi';
import { useGenerateRatifiedContractMutation } from '@/apis/extended/contractRatificationApi';
import {
  useGetCounterProposalQuery,
  useSubmitCounterProposalMutation,
  useUpdateCounterProposalMutation,
} from '@/apis/extended/negotiation';
import {
  useCreateBatchProposalsMutation,
  useCreateProposalMutation,
  useGetContractProposalsQuery,
} from '@/apis/extended/proposalApi';
import { ClauseProposal } from '@/components/Clause/ClauseProposal';
import { useContractClauseCategories } from '@/components/Layout/SellerAgent/Constant/contractclause';
import { PartySignersPanel } from '@/components/Negotiation/PartySignersPanel';
import { ProposalSubmissionPanel } from '@/components/Negotiation/ProposalSubmissionPanel';
import { ProposeDictionaryClauseCard } from '@/components/Negotiation/ProposeDictionaryClauseCard';
import { ProposalManager } from '@/components/Proposal/ProposalManager';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { useContractData } from '@/hooks/useContractData';
import {
  ClauseStatusData,
  getRole,
  ProposalData,
  RemoveClauseStatusData,
  RemoveProposalData,
  RemoveSubsetClauseStatusData,
  SetClauseStatusData,
  SetProposalData,
  SetSubsetClauseStatusData,
  SubsetClauseStatusData,
} from '@/hooks/usestoragedata';
import { CounterProposalState } from '@/types/NegotiationType';
import {
  ClauseStatus,
  ContractClauseStatus,
  ContractStatus,
} from '@/types/StatusType';
import ContractClauseSelectStatus from './Component/ClauseStatus';
import ContractOverallStatus from './Component/ContractOverallStatus';
import NegotiationHeader from './Component/Negotiationheader';
import { ClauseStatusBadge } from './Component/statusbadge';

const NegotiateContractPage = () => {
  const { uid } = useParams();
  const navigate = useNavigate();

  const contractId = uid ?? '';

  // Get comprehensive contract data
  const {
    contract,
    isLoading: contractLoading,
    error: contractError,
    isRatified: contractRatified,
    canNegotiate,
    comprehensiveClauses,
    refetch: refetchContract,
  } = useContractData(contractId, 'agent');

  // Get the actual MongoDB _id from the contract (uid might be agentIdentity)
  const actualContractId = contract?._id?.toString() || contractId;

  const clauseCategories = useContractClauseCategories(contractId);

  // Convert to the old format for backward compatibility
  const contractClauses: { title: string; description: string }[] = [];

  clauseCategories.forEach((category) => {
    category.clauses.forEach((clause) => {
      contractClauses.push({
        title: clause.title,
        description: clause.description,
      });

      // Add sub-clauses if they exist
      if (clause.subClauses) {
        clause.subClauses.forEach((subClause) => {
          contractClauses.push({
            title: subClause.title,
            description: subClause.description,
          });
        });
      }
    });
  });

  const [selectedSection, setSelectedSection] = useState(0);
  const [showTableOfContents, setShowTableOfContents] = useState(true);

  // Get agreeable clauses from backend
  const { data: agreeableClausesData = [] } = useGetAgreeableClausesQuery(
    contractId,
    {
      skip: !contractId,
    }
  );

  // Get priority clauses from backend (NEW SIMPLE VERSION)
  const { data: priorityData } = useGetContractPrioritiesQuery(contractId, {
    skip: !contractId,
  });

  const priorityClauseIds = priorityData?.priorities || [];

  console.log(`\n⭐ PRIORITIES (SIMPLE):`);
  console.log(`   Contract: ${contractId}`);
  console.log(`   Priorities:`, priorityClauseIds);
  console.log(`   Count: ${priorityClauseIds.length}\n`);

  // API mutations
  const [markAsAgreeable] = useMarkClauseAsAgreeableMutation();
  const [unmarkAsAgreeable] = useUnmarkClauseAsAgreeableMutation();
  const [togglePriority] = useToggleClausePriorityMutation();

  // Convert to Sets for easy lookup
  const agreeableClauses = new Set(
    agreeableClausesData.map((c: any) => c.clauseId)
  );
  const prioritizedClauses = new Set(priorityClauseIds);

  //contract overall status
  const [contractOverallStatus, setContractOverallStatus] =
    useState<ContractStatus>('voided');
  const [clauseStatuses, setClauseStatuses] = useState<{
    [key: number]: ClauseStatus;
  }>({});
  const [clauseSubsetStatus, setClauseSubsetStatus] = useState<{
    [key: number]: ContractClauseStatus;
  }>({});
  const [counterProposals, setCounterProposals] =
    useState<CounterProposalState>({});

  const role = getRole();
  const cardRefs = useRef<HTMLDivElement[]>([]);
  const signatureRef = useRef<HTMLDivElement>(null);

  // Batch proposal state
  const [batchProposals, setBatchProposals] = useState<
    Map<
      string,
      {
        clauseId: string;
        clauseHierarchicalId: string;
        clauseTitle: string;
        originalValue: any;
        proposedValue: any;
        message?: string;
      }
    >
  >(new Map());

  const [updateCounterProposal, { isLoading: isUpdating }] =
    useUpdateCounterProposalMutation();
  const [createCounterProposal, { isLoading: isCreating }] =
    useSubmitCounterProposalMutation();
  const [createProposal] = useCreateProposalMutation();
  const [confirmNegotiations, { isLoading: isConfirming }] =
    useConfirmNegotiationsMutation();
  const [generateContract, { isLoading: isGenerating }] =
    useGenerateRatifiedContractMutation();

  // Get all proposals for this contract
  const { data: allProposalsData } = useGetContractProposalsQuery(
    { contractId },
    { skip: !contractId }
  );

  const allProposals = Array.isArray(allProposalsData)
    ? allProposalsData
    : allProposalsData?.proposals || [];

  const { data: counterProposal } = useGetCounterProposalQuery(uid ?? '', {
    skip: !uid,
    refetchOnMountOrArgChange: true,
  });

  const handleTOCClick = (index: number) => {
    setSelectedSection(index);
    if (index < contractClauses.length) {
      cardRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else {
      signatureRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleClauseStatusChange = (index: number, status: ClauseStatus) => {
    setClauseStatuses((prev) => ({
      ...prev,
      [index]: status,
    }));

    if (status === 'not agreeable' && !counterProposals[index]) {
      setCounterProposals((prev) => ({
        ...prev,
        [index]: {
          text: contractClauses[index].description,
          status: 'draft',
          originalText: contractClauses[index].description,
          offeredPrice: '',
          earnestMoney: '',
          closingDate: '',
          homeInspection: '',
          financingContingency: '',
        },
      }));
    }
  };

  const handleProposalCreated = async (
    clauseId: string,
    clauseHierarchicalId: string,
    originalValue: any,
    proposedValue: any,
    message?: string,
    linkedClauses?: any[]
  ) => {
    try {
      console.log('Agent creating proposal with linked clauses:', {
        clauseId,
        linkedClauses,
      });

      await createProposal({
        contractId: uid || '',
        clauseId,
        clauseHierarchicalId,
        originalValue,
        proposedValue,
        message,
        linkedClauses: linkedClauses || [],
      }).unwrap();

      const linkedText =
        linkedClauses && linkedClauses.length > 0
          ? ` with ${linkedClauses.length} linked clause${
              linkedClauses.length > 1 ? 's' : ''
            }`
          : '';
      toast.success(`Proposal submitted on behalf of seller${linkedText}`);
    } catch (error) {
      console.error('Error creating proposal:', error);
      toast.error('Failed to create proposal');
    }
  };

  const handleValueAccepted = async (clauseId: string, value: any) => {
    console.log('Agent marked clause as agreeable:', clauseId, value);

    const clause = comprehensiveClauses
      .flatMap((cat) => cat.clauses)
      .find((c) => c.id === clauseId);

    if (!clause) {
      toast.error('Clause not found');
      return;
    }

    try {
      await markAsAgreeable({
        contractId,
        clauseId,
        clauseHierarchicalId: clause.hierarchicalId || clauseId,
        agreedValue: value,
      }).unwrap();

      toast.success('Clause marked as agreeable');
    } catch (error) {
      console.error('Error marking clause as agreeable:', error);
      toast.error('Failed to mark clause as agreeable');
    }
  };

  const handleValueRejected = async (clauseId: string) => {
    console.log('Agent rejected value for clause:', clauseId);

    try {
      await unmarkAsAgreeable({ contractId, clauseId }).unwrap();
      toast.error('Clause removed from agreeable list');
    } catch (error) {
      console.error('Error unmarking clause:', error);
      toast.error('Failed to unmark clause');
    }
  };

  const handleTogglePriority = async (clauseId: string) => {
    const isPriority = !prioritizedClauses.has(clauseId);

    console.log(`\n⭐ AGENT TOGGLING PRIORITY:`);
    console.log(`   Contract ID: ${contractId}`);
    console.log(`   Clause ID: ${clauseId}`);
    console.log(`   Setting to: ${isPriority}`);
    console.log(
      `   Current priorities in state: ${
        Array.from(prioritizedClauses).join(', ') || 'none'
      }`
    );

    try {
      const result = await togglePriority({
        contractId,
        clauseId,
        isPriority,
      }).unwrap();

      console.log(`   ✓ SUCCESS:`, result);
      toast.success(isPriority ? '⭐ Priority set!' : 'Priority removed');
    } catch (error) {
      console.error('❌ FAILED:', error);
      toast.error('Failed to update priority');
    }
  };

  // Batch proposal handlers
  const handleAddToBatch = (
    clauseId: string,
    clauseHierarchicalId: string,
    clauseTitle: string,
    originalValue: any,
    proposedValue: any,
    message?: string
  ) => {
    const newBatch = new Map(batchProposals);
    newBatch.set(clauseId, {
      clauseId,
      clauseHierarchicalId,
      clauseTitle,
      originalValue,
      proposedValue,
      message,
    });
    setBatchProposals(newBatch);
  };

  const handleRemoveFromBatch = (clauseId: string) => {
    const newBatch = new Map(batchProposals);
    newBatch.delete(clauseId);
    setBatchProposals(newBatch);
  };

  const handleClearBatch = () => {
    setBatchProposals(new Map());
  };

  const handleBatchSubmitSuccess = () => {
    setBatchProposals(new Map());
  };

  const SaveCounterProposal = (
    title: string,
    value: string,
    status?: ClauseStatus
  ) => {
    try {
      SetProposalData(title, value);
      if (status) return SetClauseStatusData(title, status);
      toast.success(`${title} Counter Proposal Added`);
    } catch (error) {
      toast.error('Failed to save counter proposal');
    }
  };

  const SaveClauseStatus = (title: string, status: ClauseStatus) => {
    try {
      SetClauseStatusData(title, status);
      setClauseStatuses((prev) => ({
        ...prev,
        [title]: status,
      }));
      if (status === 'agreeable') {
        SetProposalData(title, 'Agreed to original terms');
      }
    } catch (error) {
      toast.error('Failed to save clause status');
    }
  };
  const SaveSubsetOfClauseState = (
    title: string,
    status: ContractClauseStatus
  ) => {
    try {
      if (status === null || status === undefined) {
        toast.error('Please select a  Clause Status');
      }
      SetSubsetClauseStatusData(title, status);
    } catch (error) {
      toast.error('Failed to save clause status');
    }
  };
  const CreateCounterProposal = async () => {
    try {
      const getFieldData = (title: string) => {
        const value = ProposalData(title) || '';
        const status = ClauseStatusData(title) || '';
        const subsetClauseStatus = SubsetClauseStatusData(title) || '';

        return { value, status, subsetClauseStatus };
      };

      await createCounterProposal({
        contractId: uid ?? '',
        AppraisalContingency: getFieldData('Appraisal Contingency'),
        offeredPrice: getFieldData('Offered Price'),
        EarnestMoney: getFieldData('Earnest Money Deposit'),
        ClosingDate: getFieldData('Closing Date'),
        HomeInspection: getFieldData('Home Inspection Contingency'),
        FinancingContingency: getFieldData('Financing Contingency'),
        postCloseOccupancy: getFieldData('Post Close Occupancy'),
        proposedBy: { value: role ?? '', status: '', subsetClauseStatus: '' },
        NegotiationStatus: {
          value: contractOverallStatus,
          status: '',
          subsetClauseStatus: '',
        },
        Suggested: getFieldData('Suggested'),
        Contingent: getFieldData('Contingent'),
      }).unwrap();

      toast.success('Counter proposal created');
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('counter:') ||
          key.startsWith('clauseStatus:') ||
          key.startsWith('subsetClauseStatus:')
        ) {
          RemoveProposalData(key);
          RemoveClauseStatusData(key);
          RemoveSubsetClauseStatusData(key);
        }
      });

      navigate(`/agent/counter-proposal/${uid}`);
    } catch (error) {
      toast.error('Failed to create counter proposal');
    }
  };

  const updateAllCounterProposals = async () => {
    try {
      const getFieldData = (title: string) => {
        const value = ProposalData(title) || '';
        const status = ClauseStatusData(title) || '';
        const subsetClauseStatus = SubsetClauseStatusData(title) || '';
        return { value, status, subsetClauseStatus };
      };

      const allClauseData: {
        [key: string]: {
          value: string;
          status: string;
          subsetClauseStatus: string;
        };
      } = {};
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('counter:')) {
          const clauseTitle = key.replace('counter:', '');
          const value = ProposalData(clauseTitle) || '';
          const status = ClauseStatusData(clauseTitle) || '';
          const subsetClauseStatus = SubsetClauseStatusData(clauseTitle) || '';
          allClauseData[clauseTitle] = { value, status, subsetClauseStatus };
        }
      });

      await updateCounterProposal({
        contractId: uid ?? '',
        offeredPrice: getFieldData('Offered Price'),
        EarnestMoney: getFieldData('Earnest Money Deposit'),
        ClosingDate: getFieldData('Closing Date'),
        AppraisalContingency: getFieldData('Appraisal Contingency'),
        HomeInspection: getFieldData('Home Inspection Contingency'),
        FinancingContingency: getFieldData('Financing Contingency'),
        postCloseOccupancy: getFieldData('Post Close Occupancy'),
        proposedBy: { value: role ?? '', status: '', subsetClauseStatus: '' },
        NegotiationStatus: {
          value: contractOverallStatus,
          status: '',
          subsetClauseStatus: '',
        },
        Suggested: getFieldData('Suggested'),
        Contingent: getFieldData('Contingent'),
        additionalClauses: allClauseData,
      }).unwrap();

      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('counter:') ||
          key.startsWith('clauseStatus:') ||
          key.startsWith('subsetClauseStatus:')
        ) {
          RemoveProposalData(key);
          RemoveClauseStatusData(key);
          RemoveSubsetClauseStatusData(key);
        }
      });

      navigate(`/agent/counter-proposal/${uid}`);

      toast.success('Counter proposals updated');
    } catch (error) {
      console.error('Failed to update counter proposal:', error);
      toast.error('Failed to update counter proposal');
    }
  };

  // const GetClauseStatusValue = (
  //   index: number,
  //   count: number,
  //   title: string
  // ) => {
  //   const status = ClauseStatusData(title) || '';
  //   if (status === 'agreeable') {
  //     count++;
  //   }
  //   return count;
  // };

  return (
    <div className="relative flex h-screen taste-page-chrome text-[var(--on-surface)]">
      {showTableOfContents && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-30 md:hidden"
          onClick={() => setShowTableOfContents(false)}
        />
      )}
      {showTableOfContents && (
        <>
          <div className="fixed left-0 top-0 z-30 flex h-full w-72 gap-4 overflow-y-auto scroll-auto bg-[var(--surface-container-low)] p-6 transition-transform duration-300 md:static md:block md:w-1/4 md:translate-x-0">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-display mb-2 text-xl font-bold text-[var(--on-surface)]">
                Table of Contents
              </h2>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setShowTableOfContents(false)}
                aria-label="Close sidebar"
              >
                ×
              </Button>
            </div>

            <p className="mb-4 text-sm text-[var(--on-surface-variant)]">
              Select a section to view details
            </p>
            <div className="space-y-2">
              {/* Priority Clauses */}
              {prioritizedClauses.size > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-yellow-400 mb-2 px-2">
                    ⭐ PRIORITY
                  </div>
                  {comprehensiveClauses.map((category) =>
                    category.clauses
                      .filter(
                        (clause) =>
                          prioritizedClauses.has(clause.id) &&
                          !agreeableClauses.has(clause.id)
                      )
                      .map((clause) => (
                        <button
                          key={clause.id}
                          onClick={() => {
                            const element = document.getElementById(
                              `clause-${clause.id}`
                            );
                            element?.scrollIntoView({
                              behavior: 'smooth',
                              block: 'center',
                            });
                          }}
                          className="w-full text-left p-2 rounded-md text-sm transition-colors bg-yellow-900/20 border border-yellow-500/30 hover:bg-yellow-900/30"
                        >
                          <div className="flex items-center justify-between">
                            <span className="truncate text-yellow-300 font-medium">
                              {clause.title}
                            </span>
                            <ChevronRight className="h-4 w-4 flex-shrink-0 text-yellow-400" />
                          </div>
                        </button>
                      ))
                  )}
                </div>
              )}

              {/* All Clauses by Category */}
              {comprehensiveClauses.map((category, catIndex) => (
                <div key={catIndex} className="mb-3">
                  <div className="mb-2 px-2 text-sm font-semibold text-[var(--on-surface-variant)]">
                    {category.title}
                  </div>
                  {category.clauses
                    .filter(
                      (clause) =>
                        !agreeableClauses.has(clause.id) &&
                        !prioritizedClauses.has(clause.id)
                    )
                    .map((clause) => (
                      <button
                        key={clause.id}
                        onClick={() => {
                          const element = document.getElementById(
                            `clause-${clause.id}`
                          );
                          element?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                          });
                        }}
                        className="w-full rounded-md border border-transparent p-2 text-left text-sm transition-colors hover:border-[var(--primary-navy)] hover:bg-[color-mix(in_srgb,var(--primary-navy)_12%,transparent)]"
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate text-[var(--on-surface)]">
                            {clause.title}
                          </span>
                          <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        </div>
                      </button>
                    ))}
                </div>
              ))}

              {agreeableClauses.size ===
                comprehensiveClauses.flatMap((cat) => cat.clauses).length && (
                <div className="text-center py-4 text-green-400 text-sm font-medium">
                  ✓ All clauses agreed
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {/* Right Side - Content Area */}
      <div className="relative flex-1 overflow-y-auto bg-[var(--surface)] p-6 text-[var(--on-surface)]">
        <div className="flex justify-end mb-4 sticky top-0 z-10 ">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 bg-[var(--surface-container-lowest)] text-[var(--on-surface-variant)]"
            onClick={() => setShowTableOfContents(!showTableOfContents)}
          >
            <List className="h-4 w-4" />
            Contents
          </Button>
        </div>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <NegotiationHeader
            contractId={contractId}
            onBack={() => navigate(-1)}
            canNegotiate={canNegotiate}
          />

          {canNegotiate && contractId && (
            <div className="space-y-4 mb-6">
              <ProposeDictionaryClauseCard contractId={contractId} />
              {contract && <PartySignersPanel contract={contract} />}
            </div>
          )}

          {/* Proposal Management */}
          <div className="mb-8">
            <ProposalManager
              contractId={uid || ''}
              userRole="agent"
              onProposalCreated={(proposal) => {
                console.log('Proposal created via ProposalManager:', proposal);
              }}
              onProposalResponded={(proposal) => {
                console.log('Proposal responded to:', proposal);
              }}
            />
          </div>

          {/* Agent View: Show all clauses with priority highlighting */}
          {comprehensiveClauses.length > 0 ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">
                  Contract Review
                </h3>
                <div className="text-sm text-gray-400">
                  {agreeableClauses.size} of{' '}
                  {comprehensiveClauses.flatMap((cat) => cat.clauses).length}{' '}
                  clauses marked agreeable
                </div>
              </div>

              {/* Priority Clauses First */}
              {prioritizedClauses.size > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="h-1 w-1 rounded-full bg-yellow-500"></div>
                    <h4 className="text-lg font-semibold text-yellow-400">
                      Priority for Seller Review
                    </h4>
                  </div>
                  {comprehensiveClauses.map((category) =>
                    category.clauses
                      .filter(
                        (clause) =>
                          prioritizedClauses.has(clause.id) &&
                          !agreeableClauses.has(clause.id)
                      )
                      .map((clause) => (
                        <div
                          key={`priority-${clause.id}`}
                          id={`clause-${clause.id}`}
                          className="scroll-mt-20"
                        >
                          <div className="relative">
                            <div className="absolute -left-2 top-0 bottom-0 w-1 bg-yellow-500 rounded"></div>
                            <ClauseProposal
                              clauseId={clause.id}
                              clauseHierarchicalId={
                                clause.hierarchicalId || clause.id
                              }
                              title={clause.title}
                              description={clause.description}
                              originalValue={clause.originalValue}
                              currentValue={clause.value}
                              isNegotiable={clause.isNegotiable}
                              isRequired={clause.isRequired}
                              validation={clause.validation}
                              userRole="agent"
                              onProposalCreated={handleProposalCreated}
                              onValueAccepted={handleValueAccepted}
                              onValueRejected={handleValueRejected}
                              onAddToBatch={handleAddToBatch}
                              onTogglePriority={handleTogglePriority}
                              isPriority={true}
                              isSelected={batchProposals.has(clause.id)}
                              className="w-full border border-yellow-500/50 bg-[var(--surface-container-lowest)]"
                            />
                          </div>
                        </div>
                      ))
                  )}
                </div>
              )}

              {/* Regular Clauses */}
              {comprehensiveClauses.map((category, catIndex) => (
                <div key={catIndex} className="space-y-4">
                  <h4 className="text-xl font-semibold text-gray-300 mt-6">
                    {category.title}
                  </h4>
                  {category.clauses
                    .filter(
                      (clause) =>
                        !agreeableClauses.has(clause.id) &&
                        !prioritizedClauses.has(clause.id)
                    )
                    .map((clause) => (
                      <div
                        key={clause.id}
                        id={`clause-${clause.id}`}
                        className="scroll-mt-20"
                      >
                        <ClauseProposal
                          clauseId={clause.id}
                          clauseHierarchicalId={
                            clause.hierarchicalId || clause.id
                          }
                          title={clause.title}
                          description={clause.description}
                          originalValue={clause.originalValue}
                          currentValue={clause.value}
                          isNegotiable={clause.isNegotiable}
                          isRequired={clause.isRequired}
                          validation={clause.validation}
                          userRole="agent"
                          onProposalCreated={handleProposalCreated}
                          onValueAccepted={handleValueAccepted}
                          onValueRejected={handleValueRejected}
                          onAddToBatch={handleAddToBatch}
                          onTogglePriority={handleTogglePriority}
                          isPriority={prioritizedClauses.has(clause.id)}
                          isSelected={batchProposals.has(clause.id)}
                          className="w-full bg-[var(--surface-container-lowest)]"
                        />
                      </div>
                    ))}
                </div>
              ))}

              {/* Ratification Section */}
              {agreeableClauses.size ===
                comprehensiveClauses.flatMap((cat) => cat.clauses).length && (
                <Card className="bg-green-900/20 border-green-500">
                  <CardHeader>
                    <CardTitle className="text-green-400 flex items-center gap-2">
                      <CircleCheckBig className="h-6 w-6" />
                      All Clauses Agreed - Ready for Ratification
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-300">
                      All contract clauses have been reviewed and marked as
                      agreeable. The contract can now be ratified by the seller.
                    </p>
                    <Button
                      size="lg"
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      disabled={isConfirming || isGenerating}
                      onClick={async () => {
                        try {
                          if (!contract || !actualContractId) {
                            toast.error(
                              'Contract not found. Please refresh the page.'
                            );
                            console.error('Missing contract data:', {
                              contract,
                              actualContractId,
                              uid,
                              contractId,
                            });
                            return;
                          }

                          console.log('🚀 Starting contract finalization');
                          console.log('  uid (param):', uid);
                          console.log('  contractId (derived):', contractId);
                          console.log(
                            '  contract._id (actual):',
                            actualContractId
                          );
                          console.log(
                            '  contract.agentIdentity:',
                            (contract as any).agentIdentity
                          );

                          toast.info('Finalizing contract for signatures...');

                          // Step 1: Confirm negotiations complete
                          console.log(
                            'Step 1: Confirming negotiations for contract:',
                            actualContractId
                          );
                          await confirmNegotiations({
                            contractId: actualContractId,
                          }).unwrap();
                          console.log('✅ Negotiations confirmed');
                          toast.success('Negotiations confirmed!');

                          // Step 2: Generate the ratified contract
                          console.log(
                            'Step 2: Generating ratified contract...'
                          );
                          await generateContract({
                            contractId: actualContractId,
                          }).unwrap();
                          console.log('✅ Contract generated');
                          toast.success(
                            'Contract generated! Seller can now sign.'
                          );

                          // Refetch to update UI
                          await refetchContract();

                          // Show success message with instructions
                          toast.success(
                            'The seller has been notified and can sign the contract from their dashboard.',
                            { duration: 5000 }
                          );
                        } catch (error: any) {
                          console.error('❌ Error generating contract:', error);
                          console.error('Error details:', {
                            status: error?.status,
                            data: error?.data,
                            message: error?.message,
                            contractId: actualContractId,
                            originalUid: uid,
                          });
                          toast.error(
                            error?.data?.error ||
                              error?.data ||
                              'Failed to generate contract. Check console for details.'
                          );
                        }
                      }}
                    >
                      <CircleCheckBig className="h-5 w-5 mr-2" />
                      {isConfirming || isGenerating
                        ? 'Processing...'
                        : 'Finalize & Send to Seller'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            /* Fallback to old contractClauses if comprehensive not available */
            contractClauses.map((review, index) => (
              <div
                key={index}
                ref={(el) => {
                  if (el) {
                    cardRefs.current[index] = el;
                  }
                }}
                className="mb-8"
              >
                <Card className="bg-[var(--surface-container-lowest)] shadow-none transition-shadow duration-200 hover:shadow-ambient">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="capitalize text-xl font-semibold text-white leading-tight">
                        {review.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="text-white leading-relaxed">
                        {review.description}
                      </div>
                      <Separator className="my-2" />
                      <div className="flex items-center text-sm font-medium text-gray-300">
                        Your Response:
                      </div>
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={() => {
                            handleClauseStatusChange(index, 'agreeable');
                            setClauseSubsetStatus((prev) => ({
                              ...prev,
                              [index]: 'agreeable' as ContractClauseStatus,
                            }));
                            SaveClauseStatus(review.title, 'agreeable');
                            SaveSubsetOfClauseState(review.title, 'agreeable');
                          }}
                          className={`flex items-center gap-2 border ${
                            clauseStatuses[index] === 'agreeable'
                              ? 'border-[var(--primary-navy)] bg-[var(--primary-navy)] text-white'
                              : 'text-[var(--on-surface-variant)] hover:bg-[color-mix(in_srgb,var(--primary-navy)_8%,transparent)] hover:text-[var(--on-surface)]'
                          }`}
                        >
                          <CircleCheckBig className="h-4 w-4 text-green-500" />
                          Agreeable
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            handleClauseStatusChange(index, 'not agreeable');
                            setClauseSubsetStatus((prev) => ({
                              ...prev,
                              [index]: 'draft' as ContractClauseStatus,
                            }));
                            SaveClauseStatus(review.title, 'not agreeable');
                            SaveSubsetOfClauseState(review.title, 'draft');
                          }}
                          className={`flex cursor-pointer items-center gap-2 bg-[var(--surface-container-lowest)] text-[var(--on-surface)] ${
                            clauseStatuses[index] === 'not agreeable'
                              ? 'bg-red-500/20 border-red-500 text-red-200 hover:bg-red-500/10 hover:text-white'
                              : 'hover:bg-red-500/10 hover:text-[var(--on-surface)]'
                          }`}
                        >
                          <CircleX className="h-4 w-4 text-red-500" />
                          Not Agreeable
                        </Button>
                      </div>
                    </div>
                    {clauseStatuses[index] === 'not agreeable' && (
                      <div className="mt-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex flex-row justify-end">
                          <ContractClauseSelectStatus
                            setClauseStatus={
                              clauseSubsetStatus[index] ?? 'draft'
                            }
                            updateStatus={(status) =>
                              setClauseSubsetStatus((prev) => ({
                                ...prev,
                                [index]: status,
                              }))
                            }
                          />
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-900 mb-2">
                            Your Counter Proposal
                          </label>
                          <p className="text-xs text-gray-600 mb-3">
                            Provide your proposed changes to this clause
                          </p>
                        </div>
                        <Textarea
                          readOnly={false}
                          value={
                            counterProposals[index]?.text || review.description
                          }
                          onChange={(e) => {
                            setCounterProposals((prev) => ({
                              ...prev,
                              [index]: {
                                ...prev[index],
                                text: e.target.value,
                              },
                            }));
                          }}
                          placeholder="Describe your proposed changes to this clause..."
                          rows={6}
                          className="w-full bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        />
                        <div className="flex justify-between items-center mt-3">
                          <div className="text-sm text-gray-600">
                            {counterProposals[index]?.status ===
                              'submitted' && (
                              <span className="text-[var(--primary-navy)]">
                                Submitted on{' '}
                                {counterProposals[
                                  index
                                ]?.submittedAt?.toLocaleDateString()}
                              </span>
                            )}
                          </div>

                          <div className="flex justify-between items-center mt-3 gap-2">
                            <Button
                              onClick={() => {
                                SaveSubsetOfClauseState(
                                  review.title,
                                  clauseSubsetStatus[index]
                                );
                                SaveClauseStatus(review.title, 'not agreeable');
                                SaveCounterProposal(
                                  review.title,
                                  counterProposals[index]?.text ||
                                    review.description,
                                  'not agreeable'
                                );
                              }}
                              variant="outline"
                              size="sm"
                              className="cursor-pointer border border-[var(--primary-navy)] bg-[var(--primary-navy)] text-white hover:bg-[var(--surface-container-lowest)] hover:text-[var(--primary-navy)]"
                            >
                              <Send className="h-4 w-4 " />
                              {ProposalData(review.title)
                                ? 'Added'
                                : 'Add ' + review.title + ' Proposal'}
                            </Button>

                            <Button
                              onClick={() => {
                                RemoveProposalData(review.title);
                                RemoveClauseStatusData(review.title);
                                RemoveSubsetClauseStatusData(review.title);
                                setCounterProposals((prev) => {
                                  const updated = { ...prev };
                                  delete updated[index];
                                  return updated;
                                });
                                setClauseStatuses((prev) => {
                                  const updated = { ...prev };
                                  delete updated[index];
                                  return updated;
                                });
                                setClauseSubsetStatus((prev) => {
                                  const updated = { ...prev };
                                  delete updated[index];
                                  return updated;
                                });
                                toast.success(
                                  `${review.title} Counter Proposal Removed`
                                );
                              }}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50 hover:border-red-200"
                            >
                              <Trash2 /> Remove {review.title} Proposal
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ))
          )}

          {/* Proposal Submission Panel */}
          <ProposalSubmissionPanel
            contractId={contractId}
            selectedProposals={batchProposals}
            onClearAll={handleClearBatch}
            onRemoveProposal={handleRemoveFromBatch}
            onSubmitSuccess={handleBatchSubmitSuccess}
          />

          <div className="flex justify-end mb-4 gap-2">
            {counterProposal?.negotiation == null && (
              <Button
                onClick={CreateCounterProposal}
                disabled={isCreating}
                className="bg-[var(--primary-navy)] text-white hover:opacity-90"
              >
                <Send className="h-4 w-4 mr-2" />
                Create Counter Proposal
              </Button>
            )}
            {counterProposal?.negotiation?.contractId && (
              <Button
                className="cursor-pointer bg-[var(--primary-navy)] text-white hover:opacity-90"
                onClick={updateAllCounterProposals}
                disabled={isUpdating}
              >
                <Send className="h-4 w-4 mr-2" />
                Update Counter Proposals
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NegotiateContractPage;
